import { podarDetalles } from "./contextoInclusion";
import type { CVDocument, Estudio, Experiencia, Idioma, Otro } from "./model";

/**
 * Del documento al payload que espera Directus.
 *
 * Es la inversa de `fromDirectus` y no existia en el codigo viejo: el builder
 * arma este objeto a mano dentro de `updateCv()`, mezclado con la lectura del
 * Form y de localStorage, en unas 180 lineas. Aqui es una funcion pura que
 * recibe el documento y el original, y se puede probar.
 *
 * Directus escribe relaciones M2M anidadas en UNA sola peticion:
 *
 *   relacion: {
 *     create: [{ <entidad>_id: {...datos} }],
 *     update: [{ id: <idPivote>, <entidad>_id: {...datos} }],
 *     delete: [<idPivote>, ...]
 *   }
 *
 * Por eso guardar el documento entero es un PATCH y no doce llamadas sueltas.
 */

/** Filas con id negativo son nuevas: nunca llegaron al servidor. */
const esNueva = (fila: { id: number }) => fila.id < 0;

/**
 * Ids de pivote que estaban en el servidor y ya no estan en el documento.
 *
 * Se calcula por diferencia en vez de llevar banderas `isDeleted` por fila. El
 * builder viejo usa banderas Y ademas compara contra la BD para pillar
 * "acumulaciones de datos de otras HVs" -- sintoma de que las banderas se le
 * desincronizaban. Sin banderas no hay nada que desincronizar.
 */
const aBorrar = (
  actuales: { recordId?: number }[],
  originales: { recordId?: number }[],
): number[] => {
  const vivos = new Set(actuales.map((f) => f.recordId).filter((x): x is number => x != null));
  return originales
    .map((f) => f.recordId)
    .filter((id): id is number => id != null && !vivos.has(id));
};

const datosExperiencia = (e: Experiencia) => ({
  company_name: e.company_name,
  position: e.position,
  sector: e.sector,
  start_date: e.start_date,
  // Un trabajo actual no puede tener fecha de fin. Se fuerza aqui y no en la
  // UI: asi da igual por que camino se marque "actual".
  end_date: e.current_job ? null : e.end_date,
  current_job: e.current_job,
  details: e.details,
  achievements: e.achievements,
  status: "published",
});

const datosEstudio = (e: Estudio) => ({
  institution_name: e.institution_name,
  title_name: e.title_name,
  start_year: e.start_year,
  year: e.is_current ? "" : e.year,
  is_current: e.is_current,
  format: e.format,
  country_name: e.country_name,
  formation_level: e.formation_level,
  status: "published",
});

const datosIdioma = (l: Idioma) => ({ name: l.name, level: l.level, status: "published" });
const datosOtro = (o: Otro) => ({
  section_name: o.section_name,
  section_text: o.section_text,
  status: "published",
});

/** Bloque create/update/delete de una relacion M2M. */
const relacion = <T extends { id: number; recordId?: number }>(
  clave: string,
  actuales: T[],
  originales: T[],
  datos: (fila: T) => Record<string, unknown>,
) => ({
  create: actuales.filter(esNueva).map((f) => ({ [clave]: datos(f) })),
  update: actuales
    .filter((f) => !esNueva(f) && f.recordId != null)
    .map((f) => ({ id: f.recordId, [clave]: { id: f.id, ...datos(f) } })),
  delete: aBorrar(actuales, originales),
});

/**
 * Las colecciones M2O (skills, soft_skills, tools) son UNA fila con un array.
 *
 * Se manda `null` cuando la lista queda vacia para que Directus desvincule la
 * fila, en vez de dejar una con el array vacio que luego se lee como seccion
 * existente pero sin contenido.
 */
const listaM2O = (
  id: number | null,
  campo: string,
  valores: string[],
): Record<string, unknown> | null => {
  if (valores.length === 0) return null;
  return {
    ...(id != null ? { id } : {}),
    [campo]: valores,
    status: "published",
  };
};

export const toDirectus = (
  doc: CVDocument,
  original: CVDocument | null,
): Record<string, unknown> => {
  const vacio = { experience: [], education: [], languages: [], others: [] };
  const base = original ?? ({ ...doc, ...vacio } as CVDocument);

  return {
    name: doc.name,
    last_names: doc.last_names,
    email: doc.email,
    // El telefono se guarda como JSON serializado en una columna de texto.
    phone: doc.phone?.value ? JSON.stringify(doc.phone) : "",
    city: doc.city,
    open_to_remote: doc.open_to_remote,
    profession: doc.profession,
    age: doc.age,
    resume: doc.resume,
    is_inclusive: doc.is_inclusive,
    // Si se desactiva la HV inclusiva, el detalle se borra en vez de quedarse
    // guardado a escondidas: son datos sensibles, y la persona acaba de decir
    // que no quiere mostrarlos.
    disability_type: doc.is_inclusive ? doc.disability_type || null : null,
    disability_certificate: doc.is_inclusive ? doc.disability_certificate : false,
    // Solo lo que se ve en la hoja: lo respondido para el tipo actual.
    disability_details: doc.is_inclusive ? podarDetalles(doc.disability_type, doc.disability_details) : null,
    status: "published",

    profile_link: doc.links.length
      ? { ...(doc.meta.profileLinkId != null ? { id: doc.meta.profileLinkId } : {}), links: doc.links }
      : null,

    professional_experience: relacion(
      "professional_experience_id",
      doc.experience,
      base.experience,
      datosExperiencia,
    ),
    education: relacion("education_id", doc.education, base.education, datosEstudio),
    langs: relacion("langs_id", doc.languages, base.languages, datosIdioma),
    others: relacion("others_id", doc.others, base.others, datosOtro),

    skills: listaM2O(doc.meta.skillsId, "skill_name", doc.skills),
    soft_skills: listaM2O(doc.meta.softSkillsId, "skill_name", doc.soft_skills),
    tools: listaM2O(doc.meta.toolsId, "tools_name", doc.tools),
  };
};
